<template>
  <div class="page-container">
    <div class="page-header">
      <h1>Patients/Students</h1>
      <button class="start-activity-btn">Start an Activity</button>
      <button class="start-activity-btn stats-all-btn" @click="$router.push('/specialistStatisticsAll')">Statistics - All</button>
      <button class="start-activity-btn bulk-iep-btn" @click="showBulkUpload = true">Bulk Upload IEP</button>
      <button class="start-activity-btn bulk-iep-btn" @click="showBulkProfiles = true">Bulk Upload Profiles</button>
    </div>

    <!-- Bulk Upload Profiles Modal -->
    <div class="modal-overlay" v-if="showBulkProfiles" @click.self="closeBulkProfiles">
      <div class="modal-box">
        <div class="modal-header">
          <h2>Bulk Upload Child Profiles</h2>
          <button class="modal-close" @click="closeBulkProfiles">✕</button>
        </div>

        <div class="modal-hint">
          <span>Upload a CSV with columns: <strong>first_name, last_name, email, phone_number</strong>.</span>
          <button class="link-btn" @click="downloadProfileTemplate">Download template</button>
        </div>

        <label class="file-drop-area">
          <input type="file" accept=".csv" @change="onProfileCSV" hidden ref="profileFileInput" />
          <span v-if="!profileRows.length">Click to select a CSV file</span>
          <span v-else>{{ profileRows.length }} row(s) loaded — click to change</span>
        </label>

        <div v-if="profileRows.length" class="profile-preview">
          <table class="preview-table">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in profileRows" :key="i">
                <td>{{ row.first_name }}</td>
                <td>{{ row.last_name }}</td>
                <td>{{ row.email }}</td>
                <td>{{ row.phone_number }}</td>
                <td>
                  <span v-if="row.status === 'done'" class="bulk-status-done">✓</span>
                  <span v-else-if="row.status === 'error'" class="bulk-status-error" :title="row.error">✗</span>
                  <span v-else-if="row.status === 'uploading'" class="bulk-status-uploading">…</span>
                  <span v-else class="bulk-status-pending">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-if="profileError" class="bulk-error">{{ profileError }}</p>

        <div class="modal-actions">
          <button class="modal-btn-cancel" @click="closeBulkProfiles" :disabled="profileUploading">Cancel</button>
          <button class="modal-btn-upload" @click="uploadProfiles" :disabled="profileUploading || !profileRows.length">
            {{ profileUploading ? 'Uploading...' : 'Upload All' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk Upload IEP Modal -->
    <div class="modal-overlay" v-if="showBulkUpload" @click.self="closeBulkUpload">
      <div class="modal-box">
        <div class="modal-header">
          <h2>Bulk Upload IEP</h2>
          <button class="modal-close" @click="closeBulkUpload">✕</button>
        </div>

        <label class="file-drop-area">
          <input type="file" accept=".pdf,.docx" multiple @change="onFilesSelected" hidden ref="fileInput" />
          <span v-if="bulkFiles.length === 0">Click to select PDF or DOCX files</span>
          <span v-else>{{ bulkFiles.length }} file(s) selected — click to change</span>
        </label>

        <div v-if="bulkFiles.length" class="bulk-file-list">
          <div v-for="(item, i) in bulkFiles" :key="i" class="bulk-file-row">
            <span class="bulk-filename">{{ item.file.name }}</span>
            <select v-model="item.childID" class="bulk-student-select">
              <option value="">Select student...</option>
              <option v-for="s in bulkStudents" :key="s.child_id" :value="s.child_id">
                {{ s.first_name }} {{ s.last_name }}
              </option>
            </select>
            <span v-if="item.status === 'done'" class="bulk-status-done">✓</span>
            <span v-else-if="item.status === 'error'" class="bulk-status-error">✗</span>
            <span v-else-if="item.status === 'uploading'" class="bulk-status-uploading">…</span>
            <button class="bulk-remove-btn" @click="bulkFiles.splice(i, 1)" :disabled="bulkUploading">✕</button>
          </div>
        </div>

        <p v-if="bulkError" class="bulk-error">{{ bulkError }}</p>

        <div class="modal-actions">
          <button class="modal-btn-cancel" @click="closeBulkUpload" :disabled="bulkUploading">Cancel</button>
          <button
            class="modal-btn-upload"
            @click="uploadAll"
            :disabled="bulkUploading || !bulkFiles.length || bulkFiles.some(f => !f.childID)"
          >
            {{ bulkUploading ? 'Uploading...' : 'Upload All' }}
          </button>
        </div>
      </div>
    </div>
    <div class="stats-container">
      <div
        class="stat-card"
        v-for="(card, index) in stats"
        :key="index"
      >
        <div class="stat-circle">
          <img :src="card.image" alt="Stat icon" class="circle-image" />
        </div>
        <div class="stat-text">
          <div class="stat-label">{{ card.label }}</div>
          <div class="stat-number">{{ card.number }}</div>
          <div
            class="stat-growth"
            v-if="index === 0 || index === 1"
          >
            <span
              :class="{'arrow-up': card.growth >= 0, 'arrow-down': card.growth < 0}"
            >
              {{ card.growth >= 0 ? '▲' : '▼' }}
            </span>
            <span :class="card.growth >= 0 ? 'growth-up' : 'growth-down'">
              {{ Math.abs(card.growth) }}%
            </span>
            <span class="growth-label">this month</span>
          </div>
        </div>
        <div
          class="stat-line"
          v-if="index !== stats.length - 1"
        ></div>
      </div>
    </div>
    <div class="all-stats-container">
      <div class="row-wrapper">
        <div class="row-1">
          <h2>Caseload</h2>
          <div class="controls">
            <div class="search-bar">
              <button>
                <svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 24 24" stroke="currentColor" fill="none">
                  <circle cx="11" cy="11" r="6" stroke-width="2"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65" stroke-width="2"></line>
                </svg>
              </button>
              <input
                v-model="searchQuery"
                placeholder="Search"
            />
            </div>
            <div class="sort-by">
              <label>Sort by:</label>
                <select v-model="sortBy">
                  <option value="first">First Name</option>
                  <option value="last">Last Name</option>
                  <option value="intake">Intake Date</option>
                  <option value="dob">Date of Birth</option>
                  <option value="mostcomplete">Most Completed</option>
                  <option value="leastcomplete">Least Completed</option>
                  <option value="active">Active members</option>
                  <option value="mostrecentcomplete">Most Recent Completed</option>
                  <option value="leastrecentcomplete">Least Recent Completed</option>
                  <option value="sitename">Site Name</option>
                </select>
            </div>
          </div>
        </div>
        <div>
          <StudentTable :search-query="searchQuery" :sort-by="sortBy" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import image1 from '@/assets/SpecialistTotPatStu.jpg';
import image2 from '@/assets/SpecialistMembers.jpg';
import image3 from '@/assets/SpecialistActive.jpg';
import StudentTable from "@/components/StudentTable.vue";
import axios from "axios";

export default {
  components: {
    StudentTable
  },
  data() {
    return {
      stats: [
        {
          label: 'Total Patients/Students',
          number: 0,
          image: image1,
          growth: 0
        },
        {
          label: 'Caseload',
          number: 0,
          image: image2,
          growth: 0
        },
        {
          label: 'Active This Week',
          number: 0,
          image: image3
        }
      ],
      searchQuery: '',
      sortBy: 'first',
      showBulkUpload: false,
      bulkFiles: [],
      bulkUploading: false,
      bulkError: '',
      bulkStudents: [],
      specialistUserID: null,
      specialistID: null,
      showBulkProfiles: false,
      profileRows: [],
      profileUploading: false,
      profileError: '',
    };
  },
  mounted() {
    this.fetchMonthlyStats();
    this.fetchStudents();
  },
  methods: {
    async fetchStudents() {
      try {
        const base = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || 'http://localhost:8081';
        const authResp = await fetch(`${base}/api/auth/get-access-cookie-data`, { credentials: 'include' });
        if (!authResp.ok) return;
        const authData = await authResp.json();
        this.specialistUserID = authData.id;
        const [studentsResp, specResp] = await Promise.all([
          fetch(`${base}/api/specialist/user/${this.specialistUserID}/searchFilter`, { credentials: 'include' }),
          fetch(`${base}/api/specialist/user/${this.specialistUserID}`, { credentials: 'include' }),
        ]);
        if (studentsResp.ok) this.bulkStudents = await studentsResp.json() || [];
        if (specResp.ok) {
          const spec = await specResp.json();
          this.specialistID = spec.specialist_id;
        }
      } catch (e) {
        console.error('fetchStudents error:', e);
      }
    },
    downloadProfileTemplate() {
      const csv = 'first_name,last_name,email,phone_number\n';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'child_profiles_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
    onProfileCSV(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.$refs.profileFileInput.value = '';
      const reader = new FileReader();
      reader.onload = (ev) => {
        const lines = ev.target.result.split('\n').map(l => l.trim()).filter(l => l);
        if (lines.length < 2) { this.profileError = 'CSV must have a header row and at least one data row.'; return; }
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        this.profileRows = lines.slice(1).map(line => {
          const vals = line.split(',').map(v => v.trim());
          const row = {};
          headers.forEach((h, i) => { row[h] = vals[i] || ''; });
          row.status = '';
          row.error = '';
          return row;
        });
        this.profileError = '';
      };
      reader.readAsText(file);
    },
    closeBulkProfiles() {
      if (this.profileUploading) return;
      this.showBulkProfiles = false;
      this.profileRows = [];
      this.profileError = '';
    },
    async uploadProfiles() {
      if (!this.specialistID) { this.profileError = 'Could not determine specialist ID. Please refresh.'; return; }
      this.profileError = '';
      this.profileUploading = true;
      const base = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || 'http://localhost:8081';
      let hasError = false;

      for (const row of this.profileRows) {
        row.status = 'uploading';
        try {
          const createResp = await fetch(`${base}/api/child`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: this.specialistUserID,
              first_name: row.first_name,
              last_name: row.last_name,
              email: row.email || '',
              phone_number: row.phone_number || '',
            }),
          });
          if (!createResp.ok) {
            row.status = 'error';
            row.error = `Create failed (${createResp.status})`;
            hasError = true;
            continue;
          }
          const created = await createResp.json();
          const childID = created.child_id;

          const junctionResp = await fetch(`${base}/api/child-specialist-junction`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ child_id: childID, specialist_id: this.specialistID }),
          });
          row.status = junctionResp.ok ? 'done' : 'error';
          if (!junctionResp.ok) { row.error = 'Linked to specialist failed'; hasError = true; }
        } catch (err) {
          row.status = 'error';
          row.error = err.message;
          hasError = true;
        }
      }

      this.profileUploading = false;
      if (hasError) {
        this.profileError = 'Some rows failed. Check the ✗ rows above.';
      } else {
        setTimeout(() => this.closeBulkProfiles(), 800);
      }
    },
    onFilesSelected(e) {
      const files = Array.from(e.target.files);
      this.bulkFiles = files.map(f => ({ file: f, childID: '', status: '' }));
      this.$refs.fileInput.value = '';
    },
    closeBulkUpload() {
      if (this.bulkUploading) return;
      this.showBulkUpload = false;
      this.bulkFiles = [];
      this.bulkError = '';
    },
    async uploadAll() {
      this.bulkError = '';
      this.bulkUploading = true;
      const base = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || 'http://localhost:8081';
      let hasError = false;
      for (const item of this.bulkFiles) {
        item.status = 'uploading';
        const form = new FormData();
        form.append('file', item.file);
        try {
          const resp = await fetch(`${base}/api/iep/${item.childID}`, {
            method: 'POST',
            credentials: 'include',
            body: form,
          });
          item.status = resp.ok ? 'done' : 'error';
          if (!resp.ok) hasError = true;
        } catch (err) {
          item.status = 'error';
          hasError = true;
        }
      }
      this.bulkUploading = false;
      if (hasError) {
        this.bulkError = 'Some files failed to upload. Check the ✗ rows above.';
      } else {
        setTimeout(() => this.closeBulkUpload(), 800);
      }
    },
    async fetchMonthlyStats() {
      try {
        const baseUrl = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || "http://localhost:8081";

        const authResp = await fetch(`${baseUrl}/api/auth/get-access-cookie-data`, {
          credentials: "include",
        });
        if (!authResp.ok) throw new Error("Failed to fetch auth cookie data");
        const authData = await authResp.json();
        const ID = authData.id;

        const response = await axios.get(`${baseUrl}/api/specialist/user/${ID}/monthly-stats`);

        const data = response.data;

        this.stats[0].number = data.total_patients.total;
        this.stats[0].growth = data.total_patients.percentage_change.toFixed(0);

        this.stats[1].number = data.caseload.total;
        this.stats[1].growth = data.caseload.percentage_change.toFixed(0);

        this.stats[2].number = data.caseload.active_this_week;

      } catch (error) {
        console.error("Error fetching monthly stats:", error);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.page-container {
  padding-top: 100px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  h1 {
    font-size: 24px;
    font-weight: 600;
    color: black;
    margin: 0;
  }
  .start-activity-btn {
    background-color: #FFF5E3;
    color: #9D5f0E;
    border: none;
    border-radius: 6px;
    padding: 10px 20px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    &:hover {
      background-color: darken(#FFF5E3, 5%);
    }
  }
}
.stats-container {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 0; // we'll use fixed-width lines instead
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 20px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}
.stat-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background-color: white;
  text-align: center;
  min-width: 0;
  .stat-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
  }
  .circle-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .stat-text {
    display: flex;
    flex-direction: column;
  }
  .stat-label {
    font-size: 16px;
    color: gray;
    margin-bottom: 4px;
  }
  .stat-number {
    font-size: 24px;
    font-weight: 700;
    color: black;
  }
}
.stat-growth {
  margin-top: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.arrow-up {
  color: orange;
  font-size: 14px;
}
.arrow-down {
  color: red;
  font-size: 14px;
}
.growth-up {
  color: orange;
  font-weight: 600;
}
.growth-down {
  color: red;
  font-weight: 600;
}
.growth-label {
  color: gray;
}
.bulk-iep-btn {
  background-color: #fff8ee;
  color: #aa6d27;
  border: 1px solid #f0d9a8 !important;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-box {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  width: 600px;
  max-width: 95vw;
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  h2 { font-size: 18px; font-weight: 700; color: #1F1F1F; margin: 0; }
}
.modal-close {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #aaa;
  &:hover { color: #555; }
}
.file-drop-area {
  display: block;
  border: 2px dashed #f0d9a8;
  border-radius: 10px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  font-size: 14px;
  color: #aa6d27;
  background: #fff8ee;
  &:hover { border-color: #aa6d27; }
}
.bulk-file-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.bulk-file-row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff8ee;
  border: 1px solid #f0d9a8;
  border-radius: 8px;
  padding: 10px 12px;
}
.bulk-filename {
  flex: 1;
  font-size: 13px;
  color: #5c300b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.bulk-student-select {
  padding: 6px 10px;
  border: 1px solid #f0d9a8;
  border-radius: 6px;
  font-size: 13px;
  color: #5c300b;
  background: #fff;
  min-width: 160px;
  &:focus { outline: none; border-color: #aa6d27; }
}
.bulk-status-done     { color: #16a34a; font-weight: 700; font-size: 16px; }
.bulk-status-error    { color: #c0392b; font-weight: 700; font-size: 16px; }
.bulk-status-uploading { color: #aa6d27; font-size: 16px; }
.bulk-remove-btn {
  background: none;
  border: none;
  color: #aaa;
  cursor: pointer;
  font-size: 13px;
  padding: 0 2px;
  &:hover { color: #c0392b; }
  &:disabled { opacity: 0.4; cursor: default; }
}
.bulk-error {
  color: #c0392b;
  font-size: 13px;
  margin: 0;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
}
.modal-btn-cancel {
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: default; }
}
.modal-btn-upload {
  background: #aa6d27;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #E7A63A; }
  &:disabled { opacity: 0.5; cursor: default; }
}
.modal-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #5c300b;
  margin: 0;
  flex-wrap: wrap;
}
.link-btn {
  background: none;
  border: none;
  color: #aa6d27;
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
  padding: 0;
  flex-shrink: 0;
}
.profile-preview {
  overflow-x: auto;
}
.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th {
    text-align: left;
    padding: 8px 10px;
    color: #aa6d27;
    font-weight: 600;
    border-bottom: 1px solid #f0d9a8;
    white-space: nowrap;
  }
  td {
    padding: 8px 10px;
    border-bottom: 1px solid #fff8ee;
    color: #1F1F1F;
  }
}
.bulk-status-pending { color: #aaa; }
.stat-divider {
  width: 1px;
  background-color: #e4e7ec;
  margin: 12px 12px;
}
.all-stats-container{
  display: flex;
  justify-content: space-between;
  gap: 20px;
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 20px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);;
}
.row-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px; 
  width: 100%
}
.row-1 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 20px;
  max-width: 1200px;
  margin-top: 0.5rem;
  padding-top: 0.5rem;

  h2 {
    font-size: 20px;
    font-weight: 600;
    color: black;
    margin: 0;
    flex: 1; 
  }
  .controls {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: flex-end;
    flex-shrink: 0; 
  }
  .search-bar {
  display: flex;
  align-items: center;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f9fafb;
  align-self: left;
  button {
    background: none;
    border: none;
    padding: 10px 16px;
    cursor: pointer;
    color: #475467; 
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    .icon {
      width: 20px;
      height: 20px;
      stroke-width: 2;
    }
  }
  input {
    flex: 1;
    padding: 10px 16px;
    border: none;
    outline: none;
    font-size: 16px;
  }
}
.sort-by {
  display: flex;
  align-items: center;
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 4px 12px;
  gap: 6px;

  label {
    font-size: 14px;
    color: gray;
    margin: 0;
  }
  select {
    appearance: none; 
    border: none;
    background: #f9fafb;
    font-size: 14px;
    padding: 6px 24px 6px 0;
    cursor: pointer;
    outline: none;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 20 20' fill='none' stroke='%23666' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 8l4 4 4-4' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 4px center;
    background-size: 16px 16px;
  }
}
}
</style>